import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, DatePicker, Input, message, Modal, Radio, Select, Tag, Tooltip, Typography } from "antd";
import { InfoCircleOutlined, LockOutlined, SyncOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { controller } from "../../controller";
import CarrierResolution from "../InsuranceRequests/CarrierResolution";
import {
  choicePayload,
  initialChoice,
  isChoiceComplete,
  parseCarrierConflict,
} from "../InsuranceRequests/carrierChoice";

const PURPLE = "#6B43B5";
const PURPLE_BG = "#EEEDFA";
const MUTED = "#848696";

const ORDINAL_LABELS = {
  primary: "Primary",
  secondary: "Secondary",
  tertiary: "Tertiary",
  other: "Other",
};

const FIELD_DEFS = [
  { name: "member_id", label: "Member ID" },
  { name: "contract_number", label: "Plan Group / Contract #" },
  { name: "plan_name", label: "Plan Name" },
  { name: "first_name", label: "Subscriber First Name" },
  { name: "last_name", label: "Subscriber Last Name" },
  { name: "date_of_birth", label: "Subscriber DOB", type: "date" },
  {
    name: "relation_to_member",
    label: "Patient Relation",
    type: "select",
    options: [
      { value: "self", label: "Self" },
      { value: "spouse", label: "Spouse" },
      { value: "child", label: "Child" },
      { value: "other", label: "Other" },
    ],
  },
];

function fieldLabelWithLock(label, isLocked) {
  if (!isLocked) return label;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {label}
      <Tooltip title="You edited this field. Default is to keep your value; you can flip to Use PMS if you want to overwrite.">
        <LockOutlined style={{ color: PURPLE, fontSize: 12 }} aria-label="Locked" />
      </Tooltip>
    </span>
  );
}

function formatLocalValue(name, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (name === "date_of_birth") return String(value).split("T")[0];
  return String(value);
}

function defaultActionFor(fieldName, fieldStates) {
  const state = fieldStates && fieldStates[fieldName];
  if (state === "manually_edited") return "keep_local";
  return "use_sikka";
}

export default function SikkaPolicyReviewModal({ open, onClose, onSaved, preview, patientID }) {
  // policyState: { [insuranceType]: {
  //   carrierChoice: CarrierResolution choice ({kind: "provider"|"create", …}) | null,
  //   conflict: parsed 409 from adding a carrier we already have | null,
  //   employerSkipped: boolean,
  //   fieldActions: { [fieldName]: "use_sikka" | "keep_local" | "edit" },
  //   fieldOverrides: { [fieldName]: any }
  // }}
  const [policyState, setPolicyState] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(
    function () {
      if (!preview || !preview.policies) return;
      const next = {};
      preview.policies.forEach(function (policy) {
        // Matched / suggested / set by hand → that carrier; unknown → "add it",
        // prefilled from the PMS; placeholder → nothing until the user chooses.
        const initialCarrier = initialChoice(policy);

        const actions = {};
        FIELD_DEFS.forEach(function (def) {
          if (
            policy.sikka_values &&
            policy.sikka_values[def.name] !== null &&
            policy.sikka_values[def.name] !== undefined &&
            policy.sikka_values[def.name] !== ""
          ) {
            actions[def.name] = defaultActionFor(def.name, policy.field_states);
          }
        });

        next[policy.insurance_type] = {
          carrierChoice: initialCarrier,
          conflict: null,
          employerSkipped: false,
          fieldActions: actions,
          fieldOverrides: {},
        };
      });
      setPolicyState(next);
    },
    [preview],
  );

  const allCarriersResolved = useMemo(
    function () {
      if (!preview || !preview.policies) return false;
      return preview.policies.every(function (policy) {
        const s = policyState[policy.insurance_type];
        return s && isChoiceComplete(s.carrierChoice);
      });
    },
    [preview, policyState],
  );

  function updatePolicyState(insuranceType, updater) {
    setPolicyState(function (prev) {
      const cur = prev[insuranceType] || {};
      return Object.assign({}, prev, { [insuranceType]: updater(cur) });
    });
  }

  function setCarrierChoice(insuranceType, value) {
    updatePolicyState(insuranceType, function (cur) {
      // A new choice answers any "we already have this carrier" question.
      return Object.assign({}, cur, { carrierChoice: value, conflict: null });
    });
  }

  function setFieldAction(insuranceType, fieldName, action) {
    updatePolicyState(insuranceType, function (cur) {
      const nextActions = Object.assign({}, cur.fieldActions, { [fieldName]: action });
      const nextOverrides = Object.assign({}, cur.fieldOverrides);
      if (action !== "edit") delete nextOverrides[fieldName];
      return Object.assign({}, cur, { fieldActions: nextActions, fieldOverrides: nextOverrides });
    });
  }

  function setFieldOverride(insuranceType, fieldName, value) {
    updatePolicyState(insuranceType, function (cur) {
      const nextOverrides = Object.assign({}, cur.fieldOverrides, { [fieldName]: value });
      return Object.assign({}, cur, { fieldOverrides: nextOverrides });
    });
  }

  function setEmployerSkipped(insuranceType, skipped) {
    updatePolicyState(insuranceType, function (cur) {
      return Object.assign({}, cur, { employerSkipped: skipped });
    });
  }

  async function handleSave() {
    if (!preview || !preview.policies || !preview.preview_token) return;
    if (!allCarriersResolved) {
      message.warning("Choose or add a carrier for every policy before saving.");
      return;
    }

    const policiesPayload = preview.policies.map(function (policy) {
      const s = policyState[policy.insurance_type] || {};
      const fieldsToWrite = [];
      const fieldOverrides = {};
      Object.keys(s.fieldActions || {}).forEach(function (fn) {
        const action = s.fieldActions[fn];
        if (action === "use_sikka") fieldsToWrite.push(fn);
        else if (action === "edit" && s.fieldOverrides && fn in s.fieldOverrides) {
          fieldOverrides[fn] = s.fieldOverrides[fn];
        }
      });

      // Employer is its own toggle. When kept, signal via fields_to_write.
      const employer = policy.employer;
      if (employer && !s.employerSkipped) fieldsToWrite.push("employer_name");

      return {
        insurance_type: policy.insurance_type,
        carrier_choice: choicePayload(s.carrierChoice),
        fields_to_write: fieldsToWrite,
        field_overrides: fieldOverrides,
      };
    });

    try {
      setIsSaving(true);
      const res = await controller.savePatientPoliciesFromSikkaPreview({
        preview_token: preview.preview_token,
        policies: policiesPayload,
      });
      if (res.status < 250) {
        message.success("Policies saved from PMS");
        if (onSaved) onSaved(res.json);
        onClose();
      } else {
        // "We already have this carrier" is answered inside that policy's card.
        const conflict = parseCarrierConflict(res.json);
        if (conflict) {
          const type = (res.json && res.json.insurance_type) || (preview.policies.find(function (p) {
            const s = policyState[p.insurance_type] || {};
            return s.carrierChoice && s.carrierChoice.kind === "create";
          }) || {}).insurance_type;
          updatePolicyState(type, function (cur) { return Object.assign({}, cur, { conflict: conflict }); });
          return;
        }
        const err = (res.json && (res.json.error || res.json.detail)) || "Save failed";
        message.error(err);
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong while saving.");
    } finally {
      setIsSaving(false);
    }
  }

  function renderCarrierSection(policy) {
    const s = policyState[policy.insurance_type] || {};
    return (
      <div style={{ marginBottom: 16 }}>
        <Typography.Text strong style={{ color: PURPLE, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Carrier
        </Typography.Text>
        <div style={{ marginTop: 6 }}>
          <CarrierResolution
            preview={policy}
            choice={s.carrierChoice}
            conflict={s.conflict}
            onChange={function (value) { setCarrierChoice(policy.insurance_type, value); }}
          />
        </div>
      </div>
    );
  }

  function renderFieldRow(policy, def) {
    const s = policyState[policy.insurance_type] || {};
    const sikkaValue = (policy.sikka_values || {})[def.name];
    if (sikkaValue === null || sikkaValue === undefined || sikkaValue === "") return null;

    const localValue = (policy.local_snapshot || {})[def.name];
    const fieldStates = policy.field_states || {};
    const isLocked = fieldStates[def.name] === "manually_edited";
    const action = (s.fieldActions || {})[def.name] || defaultActionFor(def.name, fieldStates);

    function renderEditor() {
      if (def.type === "date") {
        const dval = (s.fieldOverrides || {})[def.name];
        return (
          <DatePicker
            format="YYYY-MM-DD"
            value={dval ? dayjs(dval, "YYYY-MM-DD") : sikkaValue ? dayjs(String(sikkaValue).split("T")[0], "YYYY-MM-DD") : null}
            onChange={function (d) {
              setFieldOverride(policy.insurance_type, def.name, d ? d.format("YYYY-MM-DD") : "");
            }}
            style={{ width: "100%" }}
          />
        );
      }
      if (def.type === "select") {
        const sval = (s.fieldOverrides || {})[def.name];
        return (
          <Select
            allowClear
            placeholder="Select"
            value={sval !== undefined ? sval : sikkaValue}
            options={def.options}
            onChange={function (v) {
              setFieldOverride(policy.insurance_type, def.name, v || "");
            }}
            style={{ width: "100%" }}
          />
        );
      }
      const tval = (s.fieldOverrides || {})[def.name];
      return (
        <Input
          value={tval !== undefined ? tval : sikkaValue || ""}
          onChange={function (e) {
            setFieldOverride(policy.insurance_type, def.name, e.target.value);
          }}
        />
      );
    }

    return (
      <div
        key={def.name}
        style={{
          display: "grid",
          gridTemplateColumns: "200px 1fr 1fr 200px",
          alignItems: "center",
          gap: 12,
          padding: "8px 0",
          borderBottom: "1px solid #f0eaf7",
        }}
      >
        <div style={{ fontSize: 13, color: "#333" }}>{fieldLabelWithLock(def.label, isLocked)}</div>
        <div style={{ fontSize: 13, color: "#333" }}>
          <span style={{ fontSize: 11, color: MUTED }}>PMS:</span> {formatLocalValue(def.name, sikkaValue)}
        </div>
        <div style={{ fontSize: 13, color: MUTED }}>
          <span style={{ fontSize: 11 }}>Local:</span> {formatLocalValue(def.name, localValue)}
        </div>
        <div>
          <Radio.Group
            size="small"
            value={action}
            onChange={function (e) {
              setFieldAction(policy.insurance_type, def.name, e.target.value);
            }}
            optionType="button"
          >
            <Radio.Button value="use_sikka">Use PMS</Radio.Button>
            <Radio.Button value="keep_local">Keep local</Radio.Button>
            <Radio.Button value="edit">Edit</Radio.Button>
          </Radio.Group>
          {action === "edit" ? <div style={{ marginTop: 6 }}>{renderEditor()}</div> : null}
        </div>
      </div>
    );
  }

  function renderEmployerSection(policy) {
    const employer = policy.employer;
    const s = policyState[policy.insurance_type] || {};
    if (!employer) return null;
    return (
      <div style={{ marginTop: 12, padding: "10px 14px", background: PURPLE_BG, borderRadius: 6 }}>
        <Typography.Text strong style={{ color: PURPLE }}>Employer:</Typography.Text>{" "}
        <Typography.Text>{employer.name || "—"}</Typography.Text>
        {employer.sikka_employer_id ? (
          <span style={{ marginLeft: 8, color: MUTED, fontSize: 12 }}>(id {employer.sikka_employer_id})</span>
        ) : null}
        <Button
          size="small"
          type={s.employerSkipped ? "default" : "link"}
          onClick={function () { setEmployerSkipped(policy.insurance_type, !s.employerSkipped); }}
          style={{ marginLeft: 12 }}
        >
          {s.employerSkipped ? "Include employer" : "Skip employer"}
        </Button>
      </div>
    );
  }

  function renderPolicyCard(policy) {
    return (
      <div
        key={policy.insurance_type}
        style={{
          border: `1px solid ${PURPLE_BG}`,
          borderRadius: 8,
          padding: "16px 18px",
          marginBottom: 18,
          boxShadow: "0 0 6px rgba(107, 67, 181, 0.08)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <Typography.Title level={5} style={{ margin: 0, color: PURPLE }}>
            {ORDINAL_LABELS[policy.insurance_type] || policy.insurance_type} Policy
          </Typography.Title>
          {policy.existing_local_policy_id ? (
            <Tag color="purple">Updates existing local policy</Tag>
          ) : (
            <Tag color="green">New policy</Tag>
          )}
        </div>

        {renderCarrierSection(policy)}

        <div style={{ marginTop: 16 }}>
          <Typography.Text strong style={{ color: PURPLE, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Fields
          </Typography.Text>
          <div style={{ marginTop: 8 }}>
            {FIELD_DEFS.map(function (def) { return renderFieldRow(policy, def); })}
          </div>
        </div>

        {renderEmployerSection(policy)}
      </div>
    );
  }

  if (!preview) return null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      maskClosable={false}
      width={1100}
      footer={null}
      zIndex={1200}
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <SyncOutlined style={{ color: PURPLE }} />
          Review insurance from PMS
          <Tooltip title="Nothing is saved until you click Save changes. Locked fields default to keeping your local value; you can flip to Use PMS per field.">
            <InfoCircleOutlined style={{ color: MUTED }} />
          </Tooltip>
        </span>
      }
    >
      {(preview.warnings || []).map(function (w, i) {
        return <Alert key={i} type="warning" showIcon message={w} style={{ marginBottom: 8 }} />;
      })}
      <div style={{ maxHeight: "65vh", overflowY: "auto", padding: "4px 4px" }}>
        {preview.policies.map(function (policy) { return renderPolicyCard(policy); })}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button
          type="primary"
          loading={isSaving}
          disabled={!allCarriersResolved}
          onClick={handleSave}
        >
          Save changes
        </Button>
      </div>
    </Modal>
  );
}
